'use client'

import '@assistant-ui/react-markdown/styles/dot.css'

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown'
import { CheckIcon, CopyIcon } from 'lucide-react'
import { type FC, type MouseEvent, memo, useState } from 'react'
import { defaultUrlTransform } from 'react-markdown'
import { useNavigate, useParams } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { DocumentReferenceChip } from '@/components/agent/DocumentReferenceChip'
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button'
import { useDocumentOverlayOptional } from '@/components/document-overlay/overlay-context'
import { EvidenceReferenceButton } from '@/components/evidence/EvidenceReferenceButton'
import {
  documentMoment,
  isDocumentReference,
  isInAppReference,
  sourcePreviewReference,
} from '@/features/source-snippets/reference'
import { cn } from '@/utils/cn'

/**
 * The markdown sanitizer blanks every link whose scheme it does not know, so a `doc:` or an
 * `evidence:` reference would reach the `a` renderer with no address. Those two pass; every
 * other link keeps the default sanitization.
 */
export function chatUrlTransform(url: string): string {
  return isInAppReference(url) ? url : defaultUrlTransform(url)
}

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      urlTransform={chatUrlTransform}
      className="aui-md"
      components={defaultComponents}
    />
  )
}

export const MarkdownText = memo(MarkdownTextImpl)

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard()
  const onCopy = () => {
    if (!code || isCopied) return
    copyToClipboard(code)
  }

  return (
    <div className="aui-code-header-root mt-2.5 flex items-center justify-between rounded-t-lg border border-border/50 border-b-0 bg-muted/50 px-3 py-1.5 text-xs max-md:py-0.5 max-md:pr-1.5">
      <span className="aui-code-header-language font-medium text-muted-foreground lowercase">
        {language}
      </span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy} className="max-md:size-8">
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  )
}

const useCopyToClipboard = ({ copiedDuration = 3000 }: { copiedDuration?: number } = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const copyToClipboard = (value: string) => {
    if (!value) return

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), copiedDuration)
    })
  }

  return { isCopied, copyToClipboard }
}

/**
 * Markdown anchor renderer. Internal links (paths starting with ``/``) are
 * intercepted and routed via react-router so the SPA doesn't full-page reload
 * when the agent emits e.g. ``[contract.pdf](/data?doc=<id>)``. External
 * links (``http://…``, ``https://…``) open in a new tab.
 *
 * A case document the answer names — ``[IMG_8848.jpeg](doc:<document_id>)`` —
 * is no address to go to: it is drawn as a chip that opens the document's
 * detail over the page the chat is on. Where nothing can open it — outside a
 * case shell, or when what follows ``doc:`` is no document id — the name stays
 * plain text, never a dead link.
 */
export function MarkdownLink({
  className,
  href,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()
  const overlay = useDocumentOverlayOptional()
  if (isDocumentReference(href)) {
    const moment = documentMoment(href)
    // A moment of a recording opens its player there (PRD-0074); a document opens as a whole.
    const opens = overlay && moment ? () => overlay.open(moment) : undefined
    return <DocumentReferenceChip onOpen={opens}>{children}</DocumentReferenceChip>
  }
  const reference = sourcePreviewReference(href, caseId)
  const isInternal = typeof href === 'string' && href.startsWith('/')

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isInternal || !href) return
    // Let modifier-clicks fall through to the browser (open in new tab etc.).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    navigate(href)
  }

  if (reference)
    return (
      <EvidenceReferenceButton caseId={reference.caseId} referenceId={reference.referenceId}>
        {children}
      </EvidenceReferenceButton>
    )
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        'aui-md-a text-primary underline underline-offset-2 hover:text-primary/80',
        className
      )}
      {...(isInternal ? {} : { target: '_blank', rel: 'noreferrer noopener' })}
      {...rest}
    >
      {children}
    </a>
  )
}

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        'aui-md-h1 mb-2 scroll-m-20 font-semibold text-base first:mt-0 last:mb-0',
        className
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        'aui-md-h2 mt-3 mb-1.5 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0',
        className
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        'aui-md-h3 mt-2.5 mb-1 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0',
        className
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        'aui-md-h4 mt-2 mb-1 scroll-m-20 font-medium text-sm first:mt-0 last:mb-0',
        className
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn('aui-md-h5 mt-2 mb-1 font-medium text-sm first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn('aui-md-h6 mt-2 mb-1 font-medium text-sm first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn('aui-md-p my-2.5 leading-normal first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  a: MarkdownLink,
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        'aui-md-blockquote my-2.5 border-muted-foreground/30 border-l-2 pl-3 text-muted-foreground italic',
        className
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        'aui-md-ul my-2 ml-4 list-disc marker:text-muted-foreground [&>li]:mt-1',
        className
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        'aui-md-ol my-2 ml-4 list-decimal marker:text-muted-foreground [&>li]:mt-1',
        className
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn('aui-md-hr my-2 border-muted-foreground/20', className)} {...props} />
  ),
  // The wrapper scrolls a wide table sideways inside the message; without it
  // the thread viewport (overflow-x-hidden) clips the columns that do not fit.
  table: ({ className, ...props }) => (
    <div className="aui-md-table-scroll my-2 overflow-x-auto">
      <table
        className={cn(
          'aui-md-table w-full border-separate border-spacing-0 overflow-y-auto',
          className
        )}
        {...props}
      />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        'aui-md-th bg-muted px-2 py-1 text-left font-medium first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right',
        className
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        'aui-md-td border-muted-foreground/20 border-b border-l px-2 py-1 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right',
        className
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        'aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg',
        className
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li className={cn('aui-md-li leading-normal', className)} {...props} />
  ),
  sup: ({ className, ...props }) => (
    <sup className={cn('aui-md-sup [&>a]:text-xs [&>a]:no-underline', className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        'aui-md-pre overflow-x-auto rounded-t-none rounded-b-lg border border-border/50 border-t-0 bg-muted/30 p-3 text-xs leading-relaxed',
        className
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock()
    return (
      <code
        className={cn(
          !isCodeBlock &&
            'aui-md-inline-code rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[0.85em]',
          className
        )}
        {...props}
      />
    )
  },
  CodeHeader,
})
